import {
  LOGIN_SET_IS_LOADING,
  LOGIN_SET_ORGANIZATIONS,
  LOGIN_SET_USERNAME_PASSWORD,
  LOGIN_SET_MODAL_OPEN,
  LOGIN_SET_LOGIN_MODE,
  LOGIN_ERROR,
  LOGIN_SET_REDIRECT_URL,
  LOGIN_SET_MAGIC_LINK_MODEL,
} from '../constants';
import { RootState } from '../reducers';
import { post } from '../Services/http';
import { translate } from '../Services/translation';
import LoginMode from '../Types/LoginMode';
import LoginOrganization from '../Types/LoginOrganization';
import LoginResponse from '../Types/LoginResponse';
import { MagicLinkModel } from '../Types/MagicLinkModel';
import { updateRequestVerificationToken } from '../Utils/requestVerificationTokenUtility';

export const performLogin =
  (username: string, password: string) => (dispatch) => {
    dispatch(setLoginFormLoading(true));
    const redirectUrl = new URLSearchParams(
      location.search.toLowerCase()
    ).get('redirecturl');

    post(`/api/login${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`, {
      username,
      password,
      redirectUrl,
    })
      .then((res) => res.json())
      .then((response: LoginResponse) =>
        dispatch(handleLoginResponse(response))
      )
      .catch((err) => dispatch(handleErrorResponse(err)));
  };

export const selectOrganization =
  (selectedOrganizationId: string) =>
  (dispatch, getState: () => RootState) => {
    dispatch(setLoginFormLoading(true));
    const redirectUrl = getState().login.redirectUrl;
    post(
      `/api/login/organizations/${selectedOrganizationId}${
        redirectUrl ? `?redirectUrl=${redirectUrl}` : ''
      }`,
      {}
    )
      .then((res) => res.json())
      .then((response: LoginResponse) => {
        dispatch(setLoginError(response.error));
        if (response.success) {
          onSuccessfulLogin(response);
        } else {
          dispatch(setLoginFormLoading(false));
        }
      })
      .catch((err) => dispatch(handleErrorResponse(err)));
  };

export const submitChangePassword =
  (username, oldPassword, newPassword, passwordConfirmation) =>
  (dispatch, getState: () => RootState) => {
    if (newPassword !== passwordConfirmation) {
      dispatch(setLoginError(translate('login.password-mismatch')));
      return;
    }

    const { redirectUrl, magicLinkModel } = getState().login;
    dispatch(setLoginFormLoading(true));
    post(
      `/api/login/changepassword${
        redirectUrl ? `?redirectUrl=${redirectUrl}` : ''
      }`,
      {
        username,
        oldPassword,
        newPassword,
        magicLinkModel,
      }
    )
      .then((res) => res.json())
      .then((response: LoginResponse) => {
        if (response.success) {
          dispatch(handleLoginResponse(response));
        } else {
          dispatch(setLoginError(response.error));
          dispatch(setLoginFormLoading(false));
        }
      })
      .catch((err) => dispatch(handleErrorResponse(err)));
  };

export const submitForgotPassword =
  (username: string): any =>
  (dispatch): Promise<boolean> => {
    return post('/api/login/forgotpassword', { username })
      .then((res) => res.json())
      .then((response: ForgotPasswordResponse) => {
        dispatch(setLoginError(response.error));
        return response.success;
      })
      .catch((err) => dispatch(handleErrorResponse(err)))
      .finally(() => dispatch(setLoginFormLoading(false)));
  };

export const setOrganizations = (organizations: LoginOrganization[]) => ({
  type: LOGIN_SET_ORGANIZATIONS,
  payload: {
    organizations,
  },
});

export const setUsername = (username: string) => ({
  type: LOGIN_SET_USERNAME_PASSWORD,
  payload: {
    username,
  },
});

export const setPassword = (password: string) => ({
  type: LOGIN_SET_USERNAME_PASSWORD,
  payload: {
    password,
  },
});

export const setLoginModalOpen = (modalOpen: boolean) => ({
  type: LOGIN_SET_MODAL_OPEN,
  payload: {
    modalOpen,
  },
});

export const setLoginMode = (loginMode: LoginMode) => ({
  type: LOGIN_SET_LOGIN_MODE,
  payload: {
    loginMode,
  },
});

export const signOut = () => {
  post('/api/mypages/logout', null).then(() => location.reload());
};

export const submitMagicLink =
  (magicLinkModel: MagicLinkModel) => (dispatch) => {
    dispatch(setLoginFormLoading(true));
    return post('/api/login/magiclink', magicLinkModel)
      .then((res) => res.json())
      .then((response: LoginResponse) => {
        dispatch(handleLoginResponse(response));
      })
      .catch((err) => {
        dispatch(setLoginMode(LoginMode.Login));
        dispatch(setMagicLinkModel(undefined));
        return dispatch(handleErrorResponse(err));
      })
      .finally(() => dispatch(setLoginFormLoading(false)));
  };

const setMagicLinkModel = (magicLinkModel?: MagicLinkModel) => ({
  type: LOGIN_SET_MAGIC_LINK_MODEL,
  payload: {
    magicLinkModel,
  },
});

const handleErrorResponse = (err) => (dispatch) => {
  err.response.json().then((data) => dispatch(setLoginError(data.error)));
  dispatch(setLoginFormLoading(false));
};

export const setLoginError = (error?: string) => ({
  type: LOGIN_ERROR,
  payload: {
    error,
  },
});

export const setLoginFormLoading = (isLoading: boolean) => ({
  type: LOGIN_SET_IS_LOADING,
  payload: {
    isLoading,
  },
});

const handleLoginResponse = (response: LoginResponse) => (dispatch) => {
  if (!response.mustChangePassword) {
    dispatch(setLoginError(response.error));
  }

  if (response.success) {
    if (response.requestToken) {
      localStorage.setItem(
        'requestVerificationToken',
        response.requestToken
      );
    }

    updateRequestVerificationToken();
    dispatch(setRedirectUrl(response.redirectUrl));

    if (response.mustChangePassword) {
      dispatch(setLoginMode(LoginMode.ChangePassword));
      dispatch(setLoginFormLoading(false));
    } else if (!response.organizations && !response.mustChangePassword) {
      onSuccessfulLogin(response);
    } else if (response.organizations) {
      dispatch(setOrganizations(response.organizations));
      dispatch(setLoginMode(LoginMode.SelectOrganization));
      dispatch(setLoginFormLoading(false));
    }
  } else {
    dispatch(setLoginFormLoading(false));
  }
};

const setRedirectUrl = (redirectUrl?: string) => ({
  type: LOGIN_SET_REDIRECT_URL,
  payload: {
    redirectUrl,
  },
});

const onSuccessfulLogin = (response: LoginResponse) => {
  if (response.redirectUrl) {
    location.href = response.redirectUrl;
  } else {
    location.reload();
  }
};

interface ForgotPasswordResponse {
  success: boolean;
  error?: string;
}
